import { Cell } from '@pelckmans/elementis/components/table';
import { ForwardedRef, forwardRef, useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ResultsEmptyState from '../per-student/components/ResultsEmptyState';
import HorizontalScrollContextProvider from '../../context/HorizontalScrollContextProvider';
import { TableData } from '../types';
import TableGroup from './TableGroup';
import LoadingState from '../../../../../shared-components/LoadingState';
import classNames from 'classnames';
import Button from '@pelckmans/elementis/components/button';
import FilterIcon from '@pelckmans/elementis/icons/filter';
import ModalContext from '../../../../../contexts/ModalContext';
import FilterStudentsModal, { MODAL_ID } from '../exercise/components/FilterStudentsModal';
import { useResultsDataContext } from '../../context/ResultsDataContext';
import { useFilters } from '../../context/filter-context/FilterContext';

interface ResultsTableProps {
  data: TableData;
  isLoading?: boolean;
  noGroups?: boolean;
  noScorableItems?: boolean;
  detailView: boolean;
}

const ResultsTableInner = (props: ResultsTableProps & { innerRef: ForwardedRef<HTMLElement> }) => {
  const [tableEl, setTableEl] = useState<HTMLTableElement | null>(null);

  const { t } = useTranslation();

  const { showModal } = useContext(ModalContext);
  const { members, groups } = useResultsDataContext();
  const { selectedMembers, setSelectedMembers, selectedGroups, selectedSchoolYear, selectedSchool } = useFilters();

  const determineHorizontalScroll = useCallback(() => {
    if (!tableEl || !(props.innerRef && 'current' in props.innerRef && props.innerRef.current)) {
      return false;
    }

    const { width: tableWidth } = tableEl.getBoundingClientRect();
    const { width: containerWidth } = props.innerRef.current.getBoundingClientRect();

    return tableWidth > containerWidth;
  }, [props.innerRef, tableEl]);

  const [horizontalScroll, setHorizontalScroll] = useState(determineHorizontalScroll());

  useEffect(() => {
    if (!tableEl) return;
    tableEl.style.setProperty('--student-column-count', props.data.header.columns.length.toString());

    setHorizontalScroll(determineHorizontalScroll());
  }, [props.data.header.columns.length, determineHorizontalScroll, tableEl]);

  if (props.isLoading)
    return <LoadingState variant="table" aria-label={t('PAGES.RESULTS.PER_STUDENT.LOADING_STATE.A11Y_LABEL')} />;

  if (props.noGroups) {
    return <ResultsEmptyState variant="no-groups-filter" />;
  }

  // TODO: incorrect variant, should be no-scorable-items
  if (props.noScorableItems) {
    return <ResultsEmptyState variant="no-scorable-items-filter" />;
  }

  function handleSetSelectedMembers(selectedMembersPerGroup: Record<string, string[]>) {
    const concatenatedIds = Object.entries(selectedMembersPerGroup)
      .map(([groupId, memberIds]) => {
        return memberIds.map(memberId => `${groupId};${memberId}`);
      })
      .flat();

    setSelectedMembers(concatenatedIds);
  }

  return (
    <HorizontalScrollContextProvider horizontalScroll={horizontalScroll}>
      <table
        className={classNames('results-table', {
          multiple: !props.detailView,
          single: props.detailView,
        })}
        ref={el => setTableEl(el)}
      >
        <thead>
          <tr>
            <Cell.Header className="scorable-item-column" title={props.data.header.title} sticky={horizontalScroll}>
              <Cell.Header.Actions>
                <Button
                  prominence="neutral"
                  onClick={() =>
                    showModal(
                      <FilterStudentsModal
                        id={MODAL_ID}
                        groups={groups.filter(
                          g =>
                            g.acceptedMemberCount > 0 &&
                            g.schoolYear === selectedSchoolYear?.id &&
                            g.school.id === selectedSchool?.id,
                        )}
                        selectedGroups={selectedGroups}
                        membersPerGroup={members}
                        selectedMembersPerGroup={selectedMembers}
                        setSelectedMembers={handleSetSelectedMembers}
                      />,
                    )
                  }
                  startIcon={<FilterIcon data-testid="filter-members" />}
                >
                  {t('ACTIONS.FILTER')}
                </Button>
              </Cell.Header.Actions>
            </Cell.Header>
            {props.data.header.columns.map(col => (
              <Cell.Header className="student-column" key={col.id} title={col.title} />
            ))}
          </tr>
        </thead>
        {props.data.rows.map(row => (
          <tbody key={`table-body-${row.id}`}>
            <TableGroup row={row} />
          </tbody>
        ))}
      </table>
    </HorizontalScrollContextProvider>
  );
};

const ResultsTable = forwardRef<HTMLElement, ResultsTableProps>((props, container) => (
  <ResultsTableInner {...props} innerRef={container} />
));

export default ResultsTable;
